import { useState, useEffect } from "react";
import {
  Table,
  Card,
  Spin,
  Tag,
  Button,
  Typography,
  message,
  Tooltip,
  Empty,
  Modal,
  Space,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { useCompany } from "../hooks/useCompany";
import { PaymentTermsSettings } from "../components/invoices/PaymentTermsSettings";
import type { ColumnsType } from "antd/es/table";

const { Title } = Typography;

const Container = styled.div`
  padding: 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const StyledCard = styled(Card)`
  .ant-card-body {
    padding: 0;
  }
`;

interface Invoice {
  id: string;
  invoice_id: string;
  reference: string;
  state: string;
  invoice_date: string;
  due_date: string;
  total_price_incl_tax: string;
  total_price_excl_tax: string;
  total_unpaid: string;
  currency: string;
  url?: string; // Public payment/view URL from Moneybird
  public_view_code?: string;
  contact: {
    id: string;
    company_name: string;
  };
  details: Array<{
    id: string;
    description: string;
    amount: string;
    price: string;
    total_price_excl_tax: string;
    total_price_incl_tax: string;
  }>;
}

const stateColors: Record<string, string> = {
  draft: "default",
  open: "processing",
  scheduled: "warning",
  pending_payment: "warning",
  late: "error",
  reminded: "error",
  paid: "success",
  uncollectible: "default",
};

const stateLabels: Record<string, string> = {
  draft: "Draft",
  open: "Open",
  scheduled: "Scheduled",
  pending_payment: "Pending Payment",
  late: "Overdue",
  reminded: "Reminded",
  paid: "Paid",
  uncollectible: "Uncollectible",
};

export const Invoices = () => {
  const { t } = useTranslation();
  const { selectedCompany, companyDetails, companyDetailsLoading } =
    useCompany();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [preparingInvoice, setPreparingInvoice] = useState(false);

  const fetchInvoices = async (showRefreshMessage = false) => {
    // Don't proceed if company details are still loading
    if (companyDetailsLoading) {
      return;
    }

    // Don't proceed if company details aren't loaded yet
    if (!companyDetails) {
      setLoading(false);
      return;
    }

    // Check if the company has Moneybird integration
    if (!companyDetails.moneybird_contact_id) {
      message.warning(t("invoices.noMoneybirdIntegration"));
      setLoading(false);
      return;
    }

    try {
      if (showRefreshMessage) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      const response = await supabase.functions.invoke(
        "fetch-moneybird-invoices",
        {
          body: { company_id: selectedCompany },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.error) {
        throw new Error(response.error.message || "Failed to fetch invoices");
      }

      setInvoices(response.data.invoices || []);

      if (showRefreshMessage) {
        message.success(t("invoices.refreshSuccess"));
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      message.error(t("invoices.fetchError"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (selectedCompany && !companyDetailsLoading) {
      fetchInvoices();
    }
  }, [
    selectedCompany,
    companyDetailsLoading,
    companyDetails?.moneybird_contact_id,
  ]);

  const handleDownloadInvoice = async (invoice: Invoice) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      // Call our edge function to download the PDF
      const response = await supabase.functions.invoke(
        "download-moneybird-invoice-pdf",
        {
          body: {
            invoice_id: invoice.id,
            company_id: selectedCompany,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.error) {
        console.error("PDF download error:", response.error);
        message.error(t("invoices.downloadError"));
        return;
      }

      if (response.data && response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice-${invoice.invoice_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.success(t("invoices.downloadSuccess"));
      } else {
        message.error(t("invoices.downloadError"));
      }
    } catch (error) {
      console.error("Error downloading invoice PDF:", error);
      message.error(t("invoices.downloadError"));
    }
  };

  const handlePayInvoice = async (invoice: Invoice) => {
    if (!invoice.url || !invoice.public_view_code) {
      message.error(t("invoices.noPaymentLink"));
      return;
    }

    // Copy public view code to clipboard
    try {
      await navigator.clipboard.writeText(invoice.public_view_code);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }

    // Show modal with public view code
    setSelectedInvoice(invoice);
    setPaymentModalVisible(true);
    setPreparingInvoice(true);

    // After 2 seconds, open the invoice and close modal
    setTimeout(() => {
      window.open(invoice.url, "_blank");
      setPreparingInvoice(false);
      setPaymentModalVisible(false);
      setSelectedInvoice(null);
    }, 2000);
  };

  const formatCurrency = (amount: string, currency: string) => {
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: currency || "EUR",
    }).format(numAmount);
  };

  const columns: ColumnsType<Invoice> = [
    {
      title: t("invoices.invoiceNumber"),
      dataIndex: "invoice_id",
      key: "invoice_id",
      width: 130,
    },
    {
      title: t("invoices.reference"),
      dataIndex: "reference",
      key: "reference",
      width: 150,
      ellipsis: true,
    },
    {
      title: t("invoices.status"),
      dataIndex: "state",
      key: "state",
      width: 100,
      render: (state: string) => (
        <Tag color={stateColors[state] || "default"}>
          {stateLabels[state] || state}
        </Tag>
      ),
    },
    {
      title: t("invoices.invoiceDate"),
      dataIndex: "invoice_date",
      key: "invoice_date",
      width: 110,
      render: (date: string) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: t("invoices.dueDate"),
      dataIndex: "due_date",
      key: "due_date",
      width: 110,
      render: (date: string) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: t("invoices.totalExclTax"),
      dataIndex: "total_price_excl_tax",
      key: "total_price_excl_tax",
      width: 130,
      align: "right",
      render: (amount: string, record: Invoice) =>
        formatCurrency(amount, record.currency),
    },
    {
      title: t("invoices.unpaid"),
      dataIndex: "total_unpaid",
      key: "total_unpaid",
      width: 120,
      align: "right",
      render: (amount: string, record: Invoice) => {
        const numAmount = parseFloat(amount);
        return (
          <span style={{ color: numAmount > 0 ? "#ff4d4f" : undefined }}>
            {formatCurrency(amount, record.currency)}
          </span>
        );
      },
    },
    {
      title: t("invoices.accessCode"),
      dataIndex: "public_view_code",
      key: "public_view_code",
      width: 130,
      align: "center",
      render: (code: string) =>
        code ? (
          <Tooltip title={t("invoices.clickToCopy")}>
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(code);
                message.success(t("common.copied"));
              }}
              style={{ fontFamily: "monospace", fontWeight: "bold" }}
            >
              {code}
            </Button>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record: Invoice) => (
        <div style={{ display: "flex", gap: "4px" }}>
          <Tooltip title={t("invoices.downloadInvoice")}>
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadInvoice(record)}
            />
          </Tooltip>
          {record.url && (
            <Tooltip title={t("invoices.payInvoice")}>
              <Button
                type="text"
                icon={<CreditCardOutlined />}
                onClick={() => handlePayInvoice(record)}
                style={{ color: "#52c41a" }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <Container>
      <HeaderRow>
        <Space align="center" size="middle">
          <Title level={2} style={{ margin: 0 }}>
            {t("invoices.title")}
          </Title>
          <Tag color="orange" style={{ fontSize: 12, fontWeight: 600 }}>
            BETA
          </Tag>
        </Space>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={() => fetchInvoices(true)}
          loading={refreshing}
        >
          {t("invoices.refresh")}
        </Button>
      </HeaderRow>

      <div style={{ marginBottom: 24 }}>
        <PaymentTermsSettings />
      </div>

      <StyledCard>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin />
          </div>
        ) : invoices.length === 0 ? (
          <Empty
            style={{ padding: 24 }}
            description={t("invoices.noInvoices")}
          />
        ) : (
          <Table
            dataSource={invoices}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        )}
      </StyledCard>

      {/* Payment Modal */}
      <Modal
        open={paymentModalVisible}
        footer={null}
        closable={false}
        centered
        width={500}
      >
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          {preparingInvoice ? (
            <>
              <LoadingOutlined
                style={{ fontSize: 48, color: "#1890ff", marginBottom: 24 }}
              />
              <Title level={4}>{t("invoices.preparingInvoice")}</Title>
            </>
          ) : (
            <>
              <CheckCircleOutlined
                style={{ fontSize: 48, color: "#52c41a", marginBottom: 24 }}
              />
              <Title level={4}>{t("invoices.codeCopied")}</Title>
            </>
          )}

          {selectedInvoice && (
            <div style={{ marginTop: 24 }}>
              <Typography.Text type="secondary">
                {t("invoices.accessCodeLabel")}
              </Typography.Text>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: "bold",
                  fontFamily: "monospace",
                  margin: "16px 0",
                  padding: "16px",
                  background: "#f5f5f5",
                  borderRadius: 8,
                  letterSpacing: 4,
                }}
              >
                {selectedInvoice.public_view_code}
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <CheckCircleOutlined
                  style={{ color: "#52c41a", marginRight: 8 }}
                />
                {t("invoices.codeCopiedToClipboard")}
              </Typography.Text>
            </div>
          )}
        </div>
      </Modal>
    </Container>
  );
};
